import React, { forwardRef } from "react";
import { API, LOGO_URL } from "@/lib/api";

const GOLD = "#E2C275";
const GOLD_DEEP = "#B8913A";
const INK = "#070B18";
const SERIF = "Georgia, 'Times New Roman', serif";
const ROLE_TITLE = { owner: "DIRECTOR", agent: "AUTHORISED ASSOCIATE PARTNER", coworker: "AUTHORISED CO-WORKER", staff: "OFFICE EXECUTIVE" };

const cardBox = {
  width: 525, height: 300, borderRadius: 14, overflow: "hidden", position: "relative", background: INK, flexShrink: 0,
  boxShadow: "0 24px 50px rgba(0,0,0,0.6), 0 0 0 1px rgba(226,194,117,0.35)",
};

const Frame = () => (
  <>
    <div style={{ position: "absolute", inset: 9, border: `1px solid ${GOLD}`, borderRadius: 9, pointerEvents: "none" }} />
    <div style={{ position: "absolute", inset: 13, border: "0.5px solid rgba(226,194,117,0.35)", borderRadius: 7, pointerEvents: "none" }} />
  </>
);

const Rule = ({ w = 120 }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6, width: w }}>
    <div style={{ height: 1, flex: 1, background: `linear-gradient(90deg, ${GOLD}, rgba(226,194,117,0))` }} />
  </div>
);

const Star = ({ size = 7 }) => (
  <div style={{ width: size, height: size, background: GOLD, transform: "rotate(45deg)", boxShadow: `0 0 6px ${GOLD}` }} />
);

const Row = ({ label, children, testid, mono }) => (
  <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: 5 }}>
    <span style={{ color: GOLD, fontSize: 6.5, letterSpacing: 2, fontWeight: 700, width: 64, flexShrink: 0, whiteSpace: "nowrap" }}>{label}</span>
    <span data-testid={testid} style={{ color: "#F4EBD3", fontSize: mono ? 13 : 8.5, fontFamily: mono ? "monospace" : "inherit", letterSpacing: mono ? 0.6 : 0.2, lineHeight: 1.4, maxWidth: 250 }}>{children}</span>
  </div>
);

const Qr = ({ src, label, sub }) => (
  <div style={{ textAlign: "center", width: 104 }}>
    <div style={{ display: "inline-block", padding: 3, borderRadius: 10, background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DEEP})` }}>
      {src && <img src={src} alt={label} style={{ width: 80, height: 80, borderRadius: 7, background: "#FBF6E6", padding: 4, display: "block" }} />}
    </div>
    <p style={{ color: GOLD, fontSize: 7.5, letterSpacing: 1.8, marginTop: 6, fontWeight: 700 }}>{label}</p>
    <p style={{ color: "rgba(244,235,211,0.6)", fontSize: 6.3, marginTop: 1 }}>{sub}</p>
  </div>
);

const Front = ({ name, phone, address, refCode, user }) => (
  <div data-testid="visiting-card-front" style={cardBox}>
    <img src="/celestial-crescent.jpg" alt="" style={{ position: "absolute", top: 0, right: -70, height: "100%", width: 420, objectFit: "cover", objectPosition: "50% 45%" }} />
    <div style={{ position: "absolute", inset: 0, background: `linear-gradient(90deg, ${INK} 0%, ${INK} 46%, rgba(7,11,24,0.55) 68%, rgba(7,11,24,0.05) 100%)` }} />
    <Frame />
    <div style={{ position: "relative", height: "100%", width: 330, padding: "26px 28px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <img src={LOGO_URL} crossOrigin="anonymous" alt="logo" style={{ width: 44, height: 44, borderRadius: "50%", border: `1.5px solid ${GOLD}`, objectFit: "cover" }} />
        <div>
          <p style={{ color: GOLD, letterSpacing: 3.2, fontSize: 11.5, fontFamily: SERIF, fontWeight: 700 }}>BENGAL UMRAH HOUSE</p>
          <p style={{ color: "rgba(244,235,211,0.7)", fontSize: 6.3, letterSpacing: 2.2, marginTop: 3 }}>SACRED JOURNEYS · CELESTIAL GUIDANCE</p>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
        {user?.id && (
          <img src={`${API}/members/${user.id}/photo`} crossOrigin="anonymous" alt={name}
            style={{ width: 62, height: 62, borderRadius: "50%", objectFit: "cover", border: `2px solid ${GOLD}`, boxShadow: `0 0 0 3px rgba(226,194,117,0.18)`, background: "#121a33" }}
            onError={(e) => { e.currentTarget.style.display = "none"; }} />
        )}
        <div style={{ minWidth: 0 }}>
          <p data-testid="card-name" style={{ color: "#FFFFFF", fontSize: 25, fontFamily: SERIF, lineHeight: 1.1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220, textShadow: "0 2px 10px rgba(0,0,0,0.6)" }}>{name || "Your Name"}</p>
          <div style={{ marginTop: 7, display: "flex", alignItems: "center", gap: 6 }}><Star size={5} /><Rule w={110} /></div>
          <p style={{ color: GOLD, fontSize: 7, letterSpacing: 2.4, marginTop: 6, fontWeight: 700 }}>{user?.role === "agent" || user?.role === "coworker" ? `ASSOCIATE PARTNER · LEVEL ${String(user?.level || 1).padStart(2, "0")}` : (ROLE_TITLE[user?.role] || "AUTHORISED ASSOCIATE")}</p>
        </div>
      </div>
      <div>
        <Row label="MOBILE" testid="card-phone" mono>{phone || "+91 XXXXX XXXXX"}</Row>
        <Row label="OFFICE">{address || "Office address"}</Row>
        {refCode && <Row label="MEMBER ID"><span style={{ color: GOLD, letterSpacing: 1.6, fontWeight: 700 }}>{refCode}</span></Row>}
        {user?.member_no && <Row label="MEMBER NO." testid="card-member-no"><span style={{ color: GOLD, letterSpacing: 1.6, fontWeight: 700 }}>#{user.member_no}</span></Row>}
      </div>
    </div>
  </div>
);

const Back = ({ name, phone, qrs }) => (
  <div data-testid="visiting-card-back" style={cardBox}>
    <img src="/celestial-crescent.jpg" alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.35 }} />
    <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 45%, rgba(7,11,24,0.35), rgba(7,11,24,0.92) 78%)" }} />
    <Frame />
    <div style={{ position: "relative", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Star size={5} />
        <p style={{ color: GOLD, letterSpacing: 4.5, fontSize: 13, fontFamily: SERIF, fontWeight: 700 }}>BENGAL UMRAH HOUSE</p>
        <Star size={5} />
      </div>
      <p style={{ color: "rgba(244,235,211,0.75)", fontSize: 7, letterSpacing: 2.4, marginTop: 5 }}>SCAN TO CONNECT WITH {(name || "US").toUpperCase()}</p>
      <div style={{ display: "flex", gap: 14, marginTop: 16 }}>
        <Qr src={qrs.portal} label="BOOK UMRAH" sub="Customer booking" />
        <Qr src={qrs.agent} label="JOIN AS PARTNER" sub="Franchise / member" />
        <Qr src={qrs.jobs} label="JOBS ABROAD" sub="Apply online" />
      </div>
      <div style={{ marginTop: 14, height: 1, width: 300, background: `linear-gradient(90deg, rgba(226,194,117,0), ${GOLD}, rgba(226,194,117,0))` }} />
      <p style={{ color: "rgba(244,235,211,0.8)", fontSize: 7.5, marginTop: 7, letterSpacing: 1.4 }}>
        UMRAH · HAJJ · ZIYARAT · OVERSEAS JOBS{phone ? `  ·  ${phone}` : ""}
      </p>
    </div>
  </div>
);

export const PremiumCard = forwardRef(({ name, phone, address, refCode, user, qrs }, ref) => (
  <div ref={ref} data-testid="visiting-card-preview" style={{ display: "flex", flexWrap: "wrap", gap: 18, padding: 18, background: "#03050c", borderRadius: 20 }}>
    <Front name={name} phone={phone} address={address} refCode={refCode} user={user} />
    <Back name={name} phone={phone} qrs={qrs} />
  </div>
));
PremiumCard.displayName = "PremiumCard";
